import type { ReactNode } from 'react'
import type { IndexOverview } from '@/lib/market/builders/overview'

import Link from 'next/link'
import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { ChangeText } from '@/components/market/change'
import { useIndexQuote, usePriceFlash } from '@/components/market/live-provider'
import { formatLevel, formatPercent } from '@/lib/market/format'
import Sparkline from '@/components/charts/sparkline'

const IndexCard = ({ index }: { index: IndexOverview }): ReactNode => {
    const quote = useIndexQuote(index.live)
    const flash = usePriceFlash(quote.level)
    const trend = useMemo(() => [...index.series.c.slice(-60, -1), quote.level], [index.series.c, quote.level])

    return (
        <Link
            href={`/indices#${index.code.toLowerCase()}`}
            className='group flex min-w-0 flex-col justify-between gap-3 rounded-xl border border-border bg-card p-4 transition-colors hover:border-axis hover:bg-accent/40'
        >
            <div className='flex items-start justify-between gap-3'>
                <div className='min-w-0'>
                    <p className='font-mono text-xs font-semibold text-primary'>{index.short}</p>
                    <p className='truncate text-xs text-muted-foreground'>{index.name}</p>
                </div>
                <ChangeText value={quote.changePct} showIcon className='text-xs font-medium' />
            </div>

            <div className='flex items-end justify-between gap-3'>
                <div>
                    <p className={cn('rounded px-0.5 text-2xl font-semibold tracking-tight text-foreground', flash)}>{formatLevel(quote.level)}</p>
                    <p className='mt-0.5 text-[11px] text-muted-foreground'>
                        YTD <span className={cn('tabular-nums', (index.perf.ytd ?? 0) >= 0 ? 'text-up' : 'text-down')}>{formatPercent(index.perf.ytd)}</span>
                        <span className='mx-1.5 text-border'>|</span>
                        1Y <span className={cn('tabular-nums', (index.perf.y1 ?? 0) >= 0 ? 'text-up' : 'text-down')}>{formatPercent(index.perf.y1)}</span>
                    </p>
                </div>

                <Sparkline values={trend} width={96} height={36} fill />
            </div>
        </Link>
    )
}

const IndexCards = ({ indices, className }: { indices: IndexOverview[], className?: string }): ReactNode => {
    return (
        <div className={cn('grid gap-3 sm:grid-cols-3', className)}>
            {indices.map(index => <IndexCard key={index.code} index={index} />)}
        </div>
    )
}

export default IndexCards
